import { ArrowLeft, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './Drawer.module.css';

export default function Drawer({ open, onClose, title, subtitle, children, footer, width = 440 }) {
  const onCloseRef = useRef(onClose);
  const openedAtRef = useRef(0);
  const bodyRef = useRef(null);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Lock background scroll only while open. Depend on `open` alone so form
  // re-renders don't tear down/re-apply body styles (which can leave overflow stuck).
  useEffect(() => {
    if (!open) return undefined;

    openedAtRef.current = Date.now();
    const scrollY = window.scrollY;
    const prev = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overscrollBehavior: document.body.style.overscrollBehavior,
    };

    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKey);

    // Keep focusable form content scrollable inside the drawer.
    const bodyEl = bodyRef.current;
    if (bodyEl) bodyEl.scrollTop = 0;

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev.overflow;
      document.body.style.overscrollBehavior = prev.overscrollBehavior;
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.left = prev.left;
      document.body.style.right = prev.right;
      document.body.style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  if (!open) return null;

  const requestClose = () => {
    if (Date.now() - openedAtRef.current < 300) return;
    onCloseRef.current?.();
  };

  const handleClose = () => onCloseRef.current?.();

  return createPortal(
    <div
      className={styles.overlay}
      role="presentation"
      onClick={requestClose}
    >
      <aside
        className={styles.panel}
        style={{ width: `min(${width}px, 100%)` }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={handleClose}
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className={styles.headerText}>
            <h2 className={styles.title}>{title}</h2>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>

        <div ref={bodyRef} className={styles.body}>
          {children}
        </div>

        {footer && <footer className={styles.footer}>{footer}</footer>}
      </aside>
    </div>,
    document.body,
  );
}
